import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Stack, Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import AdminPanel from '../components/AdminPanel';
import FloatingActionButton from '../components/common/FloatingActionButton';
import TransactionModal from '../components/transactions/TransactionModal';
import SummaryCard from '../components/dashboard/SummaryCard';
import ForecastCashFlow from '../components/forecast/ForecastCashFlow';
import UpcomingBills from '../components/dashboard/UpcomingBills';

import { useTransactions } from '../hooks/useTransactions';
import ReportsPreview from '../components/dashboard/ReportsPreview';
import UpcomingIncome from '../components/dashboard/UpcomingIncome';
import CreditCardsWidget from '../components/dashboard/CreditCardsWidget';
import AssetAccountsWidget from '../components/dashboard/AssetAccountsWidget';
import { useFormatting } from '../context/FormattingContext';
import {
  formatMoney,
  formatDateInTimeZone,
  getMonthBoundsDateKeysInTimeZone,
  getTodayDateKeyInTimeZone,
} from '../utils/formatting';

import MilestonesWidget from '../components/milestones/MilestonesWidget';
import { ROLES } from '../constants/roles';
//import NotificationsPanel from '../components/notifications/NotificationsPanel';

const zoneHeaderGradientByKey = {
  snapshot: 'linear-gradient(135deg, rgba(39,174,96,0.14) 0%, rgba(46,125,50,0.06) 55%, rgba(255,255,255,0.95) 100%)',
  upcoming: 'linear-gradient(135deg, rgba(2,136,209,0.13) 0%, rgba(30,136,229,0.05) 55%, rgba(255,255,255,0.95) 100%)',
  planning: 'linear-gradient(135deg, rgba(15,118,110,0.14) 0%, rgba(20,184,166,0.05) 55%, rgba(255,255,255,0.95) 100%)',
  recent: 'linear-gradient(135deg, rgba(120,53,15,0.12) 0%, rgba(217,119,6,0.05) 55%, rgba(255,255,255,0.95) 100%)',
  tools: 'linear-gradient(135deg, rgba(55,65,81,0.12) 0%, rgba(107,114,128,0.05) 55%, rgba(255,255,255,0.95) 100%)',
} as const;

type ZoneHeaderKey = keyof typeof zoneHeaderGradientByKey;

const ZoneHeader = ({ title, description, zoneKey }: { title: string; description: string; zoneKey: ZoneHeaderKey }) => (
  <Paper
    sx={{
      mb: 2,
      p: { xs: 1.5, sm: 2, md: 2.25 },
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      background: zoneHeaderGradientByKey[zoneKey],
      boxShadow: '0 1px 10px rgba(15, 23, 42, 0.05)',
    }}
  >
    <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Paper>
);

const DashboardOnboardingGate = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const fullName = [auth?.user?.firstName, auth?.user?.lastName].filter(Boolean).join(' ') || auth?.user?.email || 'User';

  return (
    <Box>
      <AdminPanel />

      <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
        👋 Welcome {fullName}
      </Typography>

      {/*<Typography variant="h5" sx={{ mt: 1 }}>
        👋 Welcome {fullName}
      </Typography>*/}

      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
        Role: {auth?.user?.role}
      </Typography>

      <Box sx={{ my: 3 }}>
        <Typography variant="h6" fontWeight={600}>🎯 Ready to set up your financial profile?</Typography>
        <Typography variant="body2" color="text.secondary">
          Complete onboarding to unlock dashboard insights, transactions, reports, and planning tools.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/onboarding')} sx={{ mt: 2 }}>
          🚀 Finish onboarding
        </Button>
      </Box>
    </Box>
  );
};

const DashboardOnboardedContent = () => {
  const { auth } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const formatting = useFormatting();
  const navigate = useNavigate();
  const todayDateKey = getTodayDateKeyInTimeZone(formatting.timeZone);
  const { startDateKey: monthStartKey } = getMonthBoundsDateKeysInTimeZone(formatting.timeZone, todayDateKey);
  const { summary, transactions = [] } = useTransactions(refreshKey, {
    startDate: monthStartKey,
    endDate: todayDateKey,
  });

  const fullName =
    [auth?.user?.firstName, auth?.user?.lastName].filter(Boolean).join(' ') ||
    auth?.user?.email ||
    'User';

  const handleSuccess = () => {
    setModalOpen(false);
    setRefreshKey((prev) => prev + 1);
  };

  const monthDayFormatter = new Intl.DateTimeFormat(formatting.locale, {
    month: 'short',
    day: 'numeric',
    timeZone: formatting.timeZone,
  });
  const mtdStartLabel = monthDayFormatter.format(new Date(`${monthStartKey}T12:00:00.000Z`));
  const mtdEndLabel = monthDayFormatter.format(new Date(`${todayDateKey}T12:00:00.000Z`));
  const mtdLabel = `${mtdStartLabel}–${mtdEndLabel}`;
  const asOfLabel = `As of ${formatDateInTimeZone(todayDateKey, {
    locale: formatting.locale,
    timeZone: formatting.timeZone,
    dateStyle: 'medium',
  })}`;

  return (
    <Box>
      <AdminPanel />

      <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
        👋 Welcome {fullName}
      </Typography>

      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
        Role: {auth?.user?.role}
      </Typography>

      <FloatingActionButton onClick={() => setModalOpen(true)} />
      <TransactionModal open={modalOpen} onClose={() => setModalOpen(false)} onSuccess={handleSuccess} />

      <Stack spacing={{ xs: 3, md: 4 }} sx={{ mt: 4 }}>
        {/* Zone 1 — Household Snapshot */}
        <Box>
          <ZoneHeader
            title="Household Snapshot"
            description="Where you stand right now."
            zoneKey="snapshot"
          />
          <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="h6" fontWeight="bold">💡 Quick Overview</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your financial position at a glance.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 1.25, sm: 2 },
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
              }}
            >
              <SummaryCard fill title="Cash Flow" value={summary?.cashFlowBalance || 0} dateRange={mtdLabel} />
              <SummaryCard fill title="Income" value={summary?.income || 0} dateRange={mtdLabel} />
              <SummaryCard fill title="Expense" value={summary?.expense || 0} dateRange={mtdLabel} />
              <SummaryCard fill title="Savings" value={summary?.savings?.cumulative || 0} dateRange="Total" />
              <SummaryCard fill title="Cash Balance" value={summary?.netBalance || 0} dateRange={asOfLabel} />
              <SummaryCard fill title="Net Worth" value={summary?.netWorth || 0} dateRange={asOfLabel} />
            </Box>
          </Paper>
        </Box>

        {/* Zone 2 — What’s Coming Next */}
        <Box>
          <ZoneHeader
            title="What's Coming Next"
            description="Upcoming obligations and expected inflows."
            zoneKey="upcoming"
          />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            }}
          >
            <UpcomingBills />
            <UpcomingIncome />
          </Box>
        </Box>

        {/* Zone 3 — Plan Ahead */}
        <Box>
          <ZoneHeader
            title="Plan Ahead"
            description="Forecasted cash flow for the near term."
            zoneKey="planning"
          />
          <ForecastCashFlow />
        </Box>

        {/* Zone 4 — Recent Confirmation */}
        <Box>
          <ZoneHeader
            title="Recent Confirmation"
            description="Verify what recently posted without distracting from planning."
            zoneKey="recent"
          />
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold">🧾 Recent Activity</Typography>
            {transactions?.length > 0 ? (
              <Stack spacing={1}>
                {transactions.slice(0, 5).map((tx) => (
                  <Box key={tx._id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography>{tx.description}</Typography>
                    <Typography color={tx.type === 'expense' ? 'error.main' : 'success.main'}>
                      {formatMoney(tx.amount, formatting)}
                    </Typography>
                  </Box>
                ))}
                <Button onClick={() => navigate('/transactions')} size="small" sx={{ mt: 1 }}>
                  View All →
                </Button>
              </Stack>
            ) : (
              <Typography color="text.secondary">No recent transactions.</Typography>
            )}
          </Paper>
        </Box>

        {/* Zone 5 — Supporting Tools */}
        <Box>
          <ZoneHeader
            title="Supporting Tools"
            description="Deeper tools for trends, milestones, and account management."
            zoneKey="tools"
          />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <MilestonesWidget />
            <ReportsPreview />
            <AssetAccountsWidget />
            <CreditCardsWidget showViewAllLink />
            {/* <TimelineMini /> */}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

const Dashboard = () => {
  const { auth } = useAuth();
  const role = auth?.user?.role;
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const isNotOnboarded = auth?.user?.hasCompletedOnboarding === false;

  useEffect(() => {
    document.title = 'Dashboard | Velora Money';
  }, []);

  if (!isSuperAdmin && isNotOnboarded) {
    return <DashboardOnboardingGate />;
  }

  return <DashboardOnboardedContent />;
};

export default Dashboard;
